import { useMemo, useState } from 'react';
import type { Dependency, SheetRow } from '@/lib/api';
import type { OntologySchema } from '@/lib/useOntology';
import {
  ANNOTATION_PROPERTIES,
  classOfColumn,
  columnKind,
  columnLetter,
  currentDraft,
  DATATYPE_CHOICES,
  datatypeTerm,
  draftNeeds,
  draftStarted,
  EMPTY_DRAFT,
  isThingColumn,
  mappingProblems,
  needsInWords,
  objectPropertyRows,
  termFromWords,
  type ColumnMapping,
  type DraftPart,
  type MappingLink,
  type RelationshipDraft,
  type ValueTypeChoice,
  suggestedDomain,
} from '@/lib/columnMapping';
import { Message } from '@/components/ui/Message';
import { Button, buttonVariants } from '@/components/ui/Button';
import { fieldClass } from '@/components/ui/field';
import { PredicateHint } from '@/components/import/mappingControls';

/**
 * Page 2 of the spreadsheet mapping (ruled 2026-09-30, 4.10): the properties.
 * ObjectProperties between Entity columns, each with its domain, range and
 * context; DatatypeProperties with their domain and datatype;
 * AnnotationProperties from the set QROS supports. Every row shows the
 * declaration it adds to the ontology and one statement read from the first
 * data row.
 */

const small = 'w-full text-xs';
const pick = 'w-full min-w-28 text-xs';
const toPredicate = termFromWords;

/** An entity of the first data row as a term: its UUID as written, or the
 *  label a UUID is minted for. */
function exampleEntity(mappings: ColumnMapping[], column: string, row: SheetRow | undefined): string {
  const m = mappings.find((x) => x.column === column);
  const v = row?.[column]?.trim();
  if (!m || !v) return '?';
  return m.job === 'id' && !m.isLabel && m.idStrategy !== 'generated' ? `:${v}` : `<${v}>`;
}

export function PropertiesPage({
  mappings,
  rows,
  schema,
  onChange,
  onFocus,
  draft: held,
  onDraft,
  heading = true,
  dependencies = [],
}: {
  mappings: ColumnMapping[];
  /** The data rows, of which the first supplies each row's example statement. */
  rows: SheetRow[];
  schema?: OntologySchema | null;
  onChange: (next: ColumnMapping[]) => void;
  onFocus: (columns: string[]) => void;
  /** Off when the page's own heading is written above, over several sheets. */
  heading?: boolean;
  draft: RelationshipDraft;
  onDraft: (next: RelationshipDraft) => void;
  /** The sheet's column dependencies, from its profile: which column's values
   *  determine another's. They name the domain a value row is offered. */
  dependencies?: Dependency[];
}) {
  const first = rows[0];
  const draft = currentDraft(held, mappings);
  const setDraft = (change: (d: RelationshipDraft) => RelationshipDraft) => onDraft(currentDraft(change(draft), mappings));
  const problems = useMemo(() => mappingProblems(mappings), [mappings]);
  const entities = mappings.filter(isThingColumn).map((m) => m.column);
  const letter = (column: string) => columnLetter(mappings.findIndex((m) => m.column === column));
  const put = (column: string, patch: Partial<ColumnMapping>) =>
    onChange(mappings.map((m) => (m.column === column ? { ...m, ...patch } : m)));
  const find = (column: string) => mappings.find((m) => m.column === column);

  const rowsOf = objectPropertyRows(mappings);
  const setLink = (from: string, i: number, patch: Partial<MappingLink>) => {
    const m = find(from);
    if (m) put(from, { links: m.links.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  };
  const removeLink = (from: string, i: number) => {
    const m = find(from);
    if (m) put(from, { links: m.links.filter((_, j) => j !== i) });
  };

  const needs = draftNeeds(draft);
  const draftReady = needs.length === 0;
  const [refused, setRefused] = useState(false);
  const lacks = (part: DraftPart) => refused && needs.includes(part);
  const addLink = () => {
    const m = find(draft.from);
    if (!m || !draftReady) {
      setRefused(true);
      return;
    }
    put(draft.from, { links: [...m.links, { predicate: toPredicate(draft.predicate), to: draft.to, context: draft.context }] });
    setRefused(false);
    onDraft(EMPTY_DRAFT);
  };
  const clearDraft = () => {
    setRefused(false);
    onDraft(EMPTY_DRAFT);
  };

  const datatypes = mappings.filter((m) => columnKind(m) === 'datatype' && m.job === 'attribute');
  const annotations = mappings.filter((m) => columnKind(m) === 'annotation');
  const sheetProblem = problems.find((p) => p.column === null);
  const objectProps = (schema?.properties ?? []).filter((p) => p.kind === 'object');
  const valueProps = (schema?.properties ?? []).filter((p) => p.kind === 'datatype');

  const contextOf = (context: string) => (context ? ` @context=${classOfColumn(mappings, context)}` : '');
  const objectDeclaration = (predicate: string, domain: string, range: string, context: string) =>
    `${predicate || '?'} :type :ObjectProperty ; :domain ${domain ? classOfColumn(mappings, domain) : '?'} ; :range ${classOfColumn(mappings, range)}${contextOf(context)}`;
  const objectExample = (predicate: string, domain: string, range: string, context: string) =>
    `${domain ? exampleEntity(mappings, domain, first) : '?'} ${predicate || '?'} ${exampleEntity(mappings, range, first)}${context ? ` @context=${exampleEntity(mappings, context, first)}` : ''}`;
  const problemsOf = (column: string, fields: string[]) =>
    problems.filter((p) => p.column === column && fields.includes(p.field ?? ''));
  // A value row with no domain, refused as it stands, is offered one from
  // the sheet's analysis (ruled 2026-10-12, 61.94): a suggestion with Accept
  // in the row, never a red box; a row the sheet cannot answer says in the
  // row what to choose, and only Validate refuses.
  const domainSuggestion = (m: ColumnMapping): string | null =>
    !m.onColumn.trim() && problemsOf(m.column, ['subject']).length > 0
      ? suggestedDomain(mappings, m.column, dependencies)
      : null;
  const acceptDomain = (column: string, domain: string) => put(column, { onColumn: domain, subjectClass: '' });
  const acceptDomains = (rows: ColumnMapping[]) => {
    const chosen = new Map(rows.map((m) => [m.column, domainSuggestion(m)] as const));
    onChange(
      mappings.map((m) => {
        const d = chosen.get(m.column);
        return d ? { ...m, onColumn: d, subjectClass: '' } : m;
      }),
    );
  };
  const domainCell = (m: ColumnMapping, domain: string, subjectProblem: { text: string } | undefined) => {
    const suggestion = domainSuggestion(m);
    const invalid = Boolean(subjectProblem && domain);
    return (
      <>
        <select
          aria-label={`Domain of ${m.column}`}
          aria-invalid={invalid ? true : undefined}
          value={domain}
          onChange={(e) => acceptDomain(m.column, e.target.value)}
          className={fieldClass(invalid ? 'error' : 'normal', pick)}
        >
          <option value="">which thing…</option>
          {entities
            .filter((c) => c !== m.column)
            .map((c) => (
              <option key={c} value={c}>
                {c} ({classOfColumn(mappings, c)})
              </option>
            ))}
        </select>
        {invalid && subjectProblem && (
          <Message kind="warning" density="compact" className="mt-1">
            {subjectProblem.text}
          </Message>
        )}
        {suggestion && (
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              aria-label={`Accept the domain for ${m.column}`}
              onClick={() => acceptDomain(m.column, suggestion)}
            >
              Accept
            </Button>
            <span className="text-muted-foreground">
              Suggested: {suggestion} ({classOfColumn(mappings, suggestion)})
            </span>
          </div>
        )}
        {!invalid && !suggestion && subjectProblem && (
          <p className="mt-1 text-xs text-muted-foreground">{subjectProblem.text}</p>
        )}
      </>
    );
  };
  const domainsNote = (rows: ColumnMapping[], what: string) => {
    const open = rows.filter((m) => !m.onColumn.trim() && problemsOf(m.column, ['subject']).length > 0);
    if (open.length === 0) return null;
    const suggested = open.filter((m) => domainSuggestion(m) !== null).length;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Message kind="guidance" density="compact">
          {open.length} of {rows.length} {what} row{rows.length === 1 ? '' : 's'} {open.length === 1 ? 'does' : 'do'} not
          say which thing {open.length === 1 ? 'it is' : 'they are'} about yet (the domain).{' '}
          {suggested > 0
            ? `${suggested} carr${suggested === 1 ? 'ies' : 'y'} a suggestion from the sheet: press Accept in the row, or accept them all.`
            : 'Choose the thing column each value describes, in its row.'}
        </Message>
        {suggested > 0 && (
          <Button type="button" variant="secondary" size="sm" onClick={() => acceptDomains(rows)}>
            Accept all suggested things
          </Button>
        )}
      </div>
    );
  };

  const declares = (declaration: string | null, example: string) => (
    <div className="max-w-64 break-words font-mono text-xs">
      {declaration && <div>{declaration}</div>}
      <div className="text-muted-foreground">{example}</div>
    </div>
  );

  return (
    <div className="space-y-5">
      <datalist id="tres-onto-object-props">
        {objectProps.map((p) => (
          <option key={p.uri} value={p.uri}>
            {p.label ?? ''}
          </option>
        ))}
      </datalist>
      <datalist id="tres-onto-value-props">
        {valueProps.map((p) => (
          <option key={p.uri} value={p.uri}>
            {p.label ?? ''}
          </option>
        ))}
      </datalist>
      {heading && (
        <div>
          <h3 className="text-sm font-semibold">Page 2 — Properties</h3>
          <p className="text-xs text-muted-foreground">
            Name each link between things, and say which thing each fact, note or name is about. Each row shows,
            in the ontology's terms, what it adds to the ontology and one statement read from row 1 of your
            sheet.
          </p>
        </div>
      )}
      {sheetProblem && (
        <Message kind="warning" density="compact">
          {sheetProblem.text}
        </Message>
      )}

      <section className="space-y-2">
        <h4 className="text-sm font-semibold">
          Links between things <span className="font-normal text-muted-foreground">ObjectProperties</span>
        </h4>
        <div className="overflow-x-auto">
          <table className="tres-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Name <span className="font-normal text-muted-foreground">property</span></th>
                <th>From <span className="font-normal text-muted-foreground">domain</span></th>
                <th>To <span className="font-normal text-muted-foreground">range</span></th>
                <th>Holds in <span className="font-normal text-muted-foreground">context</span></th>
                <th>Adds to the ontology</th>
              </tr>
            </thead>
            <tbody>
              {rowsOf.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    {entities.length > 1 ? (
                      <Message kind="warning" density="compact">
                        No link between your {entities.length} thing columns ({entities.join(', ')}): their things would
                        be stored unrelated. Add each link below: the column it links from, its name, the column it
                        links to.
                      </Message>
                    ) : (
                      <span className="text-xs text-muted-foreground">No link yet. Add one below.</span>
                    )}
                  </td>
                </tr>
              )}
              {rowsOf.map((r) => {
                if (r.kind === 'column') {
                  const m = find(r.column);
                  if (!m) return null;
                  const others = entities.filter((c) => c !== m.column);
                  const domain = m.onColumn.trim();
                  const subjectProblem = problemsOf(m.column, ['subject'])[0];
                  const propertyProblem = problemsOf(m.column, ['property'])[0];
                  const focus = () => onFocus([domain, m.column, m.context].filter(Boolean));
                  return (
                    <tr key={`column:${m.column}`} aria-label={`ObjectProperty of ${m.column}`} onMouseEnter={focus} onFocus={focus}>
                      <td>
                        <button
                          type="button"
                          onClick={() => put(m.column, { relationship: false, onColumn: '', predicate: '', context: '' })}
                          className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                        >
                          Remove
                        </button>
                      </td>
                      <td className="min-w-32">
                        <input
                          type="text"
                          list="tres-onto-object-props"
                          aria-label={`Property of ${m.column}`}
                          aria-invalid={propertyProblem ? true : undefined}
                          placeholder="property"
                          defaultValue={m.predicate}
                          onBlur={(e) => put(m.column, { predicate: toPredicate(e.target.value) })}
                          className={fieldClass(propertyProblem ? 'error' : 'normal', `${small} font-mono`)}
                        />
                        {m.predicate.trim() && <PredicateHint schema={schema} text={m.predicate} column="" />}
                      </td>
                      <td>
                        <select
                          aria-label={`Domain of ${m.column}`}
                          aria-invalid={subjectProblem ? true : undefined}
                          value={domain}
                          onChange={(e) => put(m.column, { onColumn: e.target.value })}
                          className={fieldClass(subjectProblem ? 'error' : 'normal', pick)}
                        >
                          <option value="">which thing…</option>
                          {others.map((c) => (
                            <option key={c} value={c}>
                              {c} ({classOfColumn(mappings, c)})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="font-mono text-xs">
                        {m.column} ({letter(m.column)}) <span className="font-sans text-muted-foreground">{classOfColumn(mappings, m.column)}</span>
                      </td>
                      <td>
                        <select
                          aria-label={`Context of ${m.column}`}
                          value={m.context}
                          onChange={(e) => put(m.column, { context: e.target.value })}
                          className={fieldClass('normal', pick)}
                        >
                          <option value="">none</option>
                          {others
                            .filter((c) => c !== domain)
                            .map((c) => (
                              <option key={c} value={c}>
                                {c} ({classOfColumn(mappings, c)})
                              </option>
                            ))}
                        </select>
                      </td>
                      <td>
                        {subjectProblem || propertyProblem ? (
                          <Message kind="warning" density="compact">
                            {[subjectProblem?.text, propertyProblem?.text].filter(Boolean).join(' ')}
                          </Message>
                        ) : (
                          declares(objectDeclaration(m.predicate, domain, m.column, m.context), objectExample(m.predicate, domain, m.column, m.context))
                        )}
                      </td>
                    </tr>
                  );
                }
                const m = find(r.from);
                const link = m?.links[r.index];
                if (!m || !link) return null;
                const others = entities.filter((c) => c !== r.from);
                const bad = !others.includes(link.to) || !link.predicate.trim();
                const focus = () => onFocus([r.from, link.to, link.context].filter(Boolean));
                return (
                  <tr
                    key={`link:${r.from}|${link.predicate}|${link.to}|${r.index}`}
                    aria-label={`${r.from} ${link.predicate} ${link.to}`}
                    onMouseEnter={focus}
                    onFocus={focus}
                  >
                    <td>
                      <button type="button" onClick={() => removeLink(r.from, r.index)} className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
                        Remove
                      </button>
                    </td>
                    <td className="min-w-32">
                      <input
                        type="text"
                        list="tres-onto-object-props"
                        aria-label={`Property from ${r.from}`}
                        defaultValue={link.predicate}
                        onBlur={(e) => setLink(r.from, r.index, { predicate: toPredicate(e.target.value) })}
                        className={fieldClass(link.predicate.trim() ? 'normal' : 'error', `${small} font-mono`)}
                      />
                      {link.predicate.trim() && <PredicateHint schema={schema} text={link.predicate} column="" />}
                    </td>
                    <td className="font-mono text-xs">
                      {r.from} ({letter(r.from)}) <span className="font-sans text-muted-foreground">{classOfColumn(mappings, r.from)}</span>
                    </td>
                    <td>
                      <select
                        aria-label={`Range of ${r.from} ${link.predicate}`}
                        value={link.to}
                        onChange={(e) => setLink(r.from, r.index, { to: e.target.value })}
                        className={fieldClass(others.includes(link.to) ? 'normal' : 'error', pick)}
                      >
                        {!others.includes(link.to) && <option value={link.to}>{link.to || 'to which thing…'}</option>}
                        {others.map((c) => (
                          <option key={c} value={c}>
                            {c} ({classOfColumn(mappings, c)})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        aria-label={`Context of ${r.from} ${link.predicate}`}
                        value={link.context}
                        onChange={(e) => setLink(r.from, r.index, { context: e.target.value })}
                        className={fieldClass('normal', pick)}
                      >
                        <option value="">none</option>
                        {others
                          .filter((c) => c !== link.to)
                          .map((c) => (
                            <option key={c} value={c}>
                              {c} ({classOfColumn(mappings, c)})
                            </option>
                          ))}
                      </select>
                    </td>
                    <td>
                      {bad ? (
                        <Message kind="warning" density="compact">
                          {!link.predicate.trim() ? 'Name the property.' : `${link.to} is not an Entity column; choose the range column.`}
                        </Message>
                      ) : (
                        declares(objectDeclaration(link.predicate, r.from, link.to, link.context), objectExample(link.predicate, r.from, link.to, link.context))
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr onMouseEnter={() => onFocus([draft.from, draft.to, draft.context].filter(Boolean))}>
                <td>
                  <Button type="button" variant="secondary" size="sm" onClick={addLink}>
                    Add
                  </Button>
                </td>
                <td>
                  <input
                    type="text"
                    list="tres-onto-object-props"
                    aria-label="New ObjectProperty: property"
                    aria-invalid={lacks('predicate') || undefined}
                    placeholder="property"
                    value={draft.predicate}
                    onChange={(e) => setDraft((d) => ({ ...d, predicate: e.target.value }))}
                    className={fieldClass(lacks('predicate') ? 'error' : 'normal', `${small} font-mono`)}
                  />
                </td>
                <td>
                  <select
                    aria-label="New ObjectProperty: domain"
                    aria-invalid={lacks('from') || undefined}
                    value={draft.from}
                    onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                    className={fieldClass(lacks('from') ? 'error' : 'normal', pick)}
                  >
                    <option value="">which thing…</option>
                    {entities.map((c) => (
                      <option key={c} value={c}>
                        {c} ({classOfColumn(mappings, c)})
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    aria-label="New ObjectProperty: range"
                    aria-invalid={lacks('to') || undefined}
                    value={draft.to}
                    onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                    className={fieldClass(lacks('to') ? 'error' : 'normal', pick)}
                  >
                    <option value="">to which thing…</option>
                    {entities
                      .filter((c) => c !== draft.from)
                      .map((c) => (
                        <option key={c} value={c}>
                          {c} ({classOfColumn(mappings, c)})
                        </option>
                      ))}
                  </select>
                </td>
                <td>
                  <select
                    aria-label="New ObjectProperty: context"
                    value={draft.context}
                    onChange={(e) => setDraft((d) => ({ ...d, context: e.target.value }))}
                    className={fieldClass('normal', pick)}
                  >
                    <option value="">none</option>
                    {entities
                      .filter((c) => c !== draft.from && c !== draft.to)
                      .map((c) => (
                        <option key={c} value={c}>
                          {c} ({classOfColumn(mappings, c)})
                        </option>
                      ))}
                  </select>
                </td>
                <td className="font-mono text-xs text-muted-foreground">
                  {draft.from && draft.to ? objectDeclaration(toPredicate(draft.predicate), draft.from, draft.to, draft.context) : ''}
                </td>
              </tr>
              {(draftStarted(draft) || (refused && !draftReady)) && (
                <tr>
                  <td colSpan={6}>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {draftStarted(draft) && (
                        <Button type="button" variant="secondary" size="sm" onClick={clearDraft}>
                          Clear the row
                        </Button>
                      )}
                      {refused && !draftReady ? (
                        <Message kind="warning" density="compact">
                          Nothing was added. Add needs {needsInWords(needs)}.
                        </Message>
                      ) : (
                        <span>
                          {draftReady
                            ? 'This link is not added yet. Press Add to keep it.'
                            : `This link is not added yet. Add needs ${needsInWords(needs)}.`}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <h4 className="text-sm font-semibold">
          Facts about a thing <span className="font-normal text-muted-foreground">DatatypeProperties</span>
        </h4>
        {domainsNote(datatypes, 'fact')}
        <div className="overflow-x-auto">
          <table className="tres-table">
            <thead>
              <tr>
                <th>Column</th>
                <th>Name <span className="font-normal text-muted-foreground">property</span></th>
                <th>About <span className="font-normal text-muted-foreground">domain</span></th>
                <th>Kind of value <span className="font-normal text-muted-foreground">datatype</span></th>
                <th>Holds in <span className="font-normal text-muted-foreground">context</span></th>
                <th>Adds to the ontology</th>
              </tr>
            </thead>
            <tbody>
              {datatypes.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-xs text-muted-foreground">
                    No fact column. Answer a column “A fact about a thing” on page 1 to record its values.
                  </td>
                </tr>
              )}
              {datatypes.map((m) => {
                const subjectProblem = problemsOf(m.column, ['subject'])[0];
                const domain = m.onColumn.trim();
                const predicate = m.predicate.trim();
                const value = first?.[m.column]?.trim();
                const focus = () => onFocus([m.column, domain, m.context].filter(Boolean));
                return (
                  <tr key={m.column} aria-label={`DatatypeProperty ${m.column}`} onMouseEnter={focus} onFocus={focus}>
                    <td className="font-mono text-xs">
                      {m.column} ({letter(m.column)})
                    </td>
                    <td className="min-w-32">
                      <input
                        type="text"
                        list="tres-onto-value-props"
                        aria-label={`Property of ${m.column}`}
                        placeholder="property"
                        defaultValue={m.predicate}
                        onBlur={(e) => put(m.column, { predicate: toPredicate(e.target.value) })}
                        className={fieldClass('normal', `${small} font-mono`)}
                      />
                      <PredicateHint schema={schema} text={m.predicate} column={m.column} />
                    </td>
                    <td>{domainCell(m, domain, subjectProblem)}</td>
                    <td>
                      <select
                        aria-label={`Datatype of ${m.column}`}
                        value={m.valueType}
                        onChange={(e) => put(m.column, { valueType: e.target.value as ValueTypeChoice })}
                        className={fieldClass('normal', 'w-full min-w-24 text-xs')}
                      >
                        {DATATYPE_CHOICES.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.words} ({datatypeTerm(d.value)})
                          </option>
                        ))}
                      </select>
                      {m.valueType === 'datetime' && (
                        <select
                          aria-label={`Date column for ${m.column}`}
                          value={m.dateFrom}
                          onChange={(e) => put(m.column, { dateFrom: e.target.value })}
                          className={fieldClass('normal', `${pick} mt-1`)}
                        >
                          <option value="">date in this column</option>
                          {mappings
                            .filter((x) => x.column !== m.column)
                            .map((x) => (
                              <option key={x.column} value={x.column}>
                                the date is in {x.column}
                              </option>
                            ))}
                        </select>
                      )}
                    </td>
                    <td>
                      <select
                        aria-label={`Context of ${m.column}`}
                        value={m.context}
                        onChange={(e) => put(m.column, { context: e.target.value })}
                        className={fieldClass('normal', 'w-full min-w-24 text-xs')}
                      >
                        <option value="">none</option>
                        {entities
                          .filter((c) => c !== domain)
                          .map((c) => (
                            <option key={c} value={c}>
                              {c} ({classOfColumn(mappings, c)})
                            </option>
                          ))}
                      </select>
                    </td>
                    <td>
                      {declares(`${predicate || '?'} :type :DatatypeProperty ; :domain ${domain ? classOfColumn(mappings, domain) : '?'} ; :range ${datatypeTerm(m.valueType)}${contextOf(m.context)}`, `${domain ? exampleEntity(mappings, domain, first) : '?'} ${predicate || '?'} ${value ? `"${value}"^^${datatypeTerm(m.valueType)}` : '?'}${m.context ? ` @context=${exampleEntity(mappings, m.context, first)}` : ''}`)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <h4 className="text-sm font-semibold">
          Notes and names <span className="font-normal text-muted-foreground">AnnotationProperties</span>
        </h4>
        {domainsNote(annotations, 'note')}
        <div className="overflow-x-auto">
          <table className="tres-table">
            <thead>
              <tr>
                <th>Column</th>
                <th>Which note <span className="font-normal text-muted-foreground">property</span></th>
                <th>About <span className="font-normal text-muted-foreground">domain</span></th>
                <th>Adds to the ontology</th>
              </tr>
            </thead>
            <tbody>
              {annotations.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-xs text-muted-foreground">
                    No note or name column. Answer a column “A note or name” on page 1 for a name, a comment or a
                    definition.
                  </td>
                </tr>
              )}
              {annotations.map((m) => {
                const subjectProblem = problemsOf(m.column, ['subject'])[0];
                const domain = m.onColumn.trim();
                const value = first?.[m.column]?.trim();
                const focus = () => onFocus([m.column, domain].filter(Boolean));
                return (
                  <tr key={m.column} aria-label={`AnnotationProperty ${m.column}`} onMouseEnter={focus} onFocus={focus}>
                    <td className="font-mono text-xs">
                      {m.column} ({letter(m.column)})
                    </td>
                    <td>
                      <select
                        aria-label={`Property of ${m.column}`}
                        value={m.predicate.trim()}
                        onChange={(e) => put(m.column, { predicate: e.target.value })}
                        className={fieldClass('normal', pick)}
                      >
                        {ANNOTATION_PROPERTIES.map((a) => (
                          <option key={a.predicate} value={a.predicate}>
                            {a.label} ({a.predicate})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{domainCell(m, domain, subjectProblem)}</td>
                    <td>
                      {declares(null, `${domain ? exampleEntity(mappings, domain, first) : '?'} ${m.predicate.trim()} ${value ? `"${value}"` : '?'}`)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
